import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useLAScope } from '../contexts/LAScopeContext';
import { DigitalTierBadge } from '../components/DigitalScore';
import { ClaimBadge, BDAFDots } from '../components/BDAFDots';
import { QuotaLimitCard, isQuotaError } from '../components/QuotaLimitCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, Download, ChevronLeft, ChevronRight, Building2, Wifi, MapPin } from 'lucide-react';
import { DistrictShareInline } from '../components/DistrictShare';

export default function SearchPage() {
  const { laScope } = useLAScope();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [district, setDistrict] = useState(searchParams.get('district') || '');
  const [chStatus, setChStatus] = useState('');
  const [digitalTier, setDigitalTier] = useState(searchParams.get('tier') || '');
  const [sector, setSector] = useState('');
  const [results, setResults] = useState({ businesses: [], total: 0, page: 1, pages: 1 });
  const [quotaHit, setQuotaHit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [districtMeta, setDistrictMeta] = useState(null);

  useEffect(() => {
    if (!district) { setDistrictMeta(null); return; }
    api.get('/postcodes')
      .then(r => {
        const found = (r.data || []).find(p => p.district === district);
        if (found) {
          const c1 = found.cat1_trading_invisible || 0;
          const c2 = found.cat2_shell_companies || 0;
          const c3 = found.cat3_dissolved_winding_down || 0;
          const inv = (c1 + c2 + c3) || (found.digitally_invisible || 0);
          const pct = found.total_businesses ? (inv / found.total_businesses) * 100 : null;
          setDistrictMeta({ name: found.name, invisiblePct: pct, total: found.total_businesses, invisible: inv, risk: found.connectivity_risk });
        } else setDistrictMeta(null);
      })
      .catch(() => setDistrictMeta(null));
  }, [district]);

  const doSearch = async (p = 1) => {
    setLoading(true);
    try {
      const { data } = await api.post('/businesses/search', {
        query, postcode_district: district, ch_status: chStatus, digital_tier: digitalTier, sector, page: p, limit: 20,
        ...(laScope && laScope !== 'tvca' ? { local_authority: laScope } : {}),
      });
      setResults(data);
      setQuotaHit(null);
      setPage(p);
    } catch (e) {
      if (isQuotaError(e)) { setQuotaHit(e.response.data.detail); setResults({ businesses: [], total: 0, page: 1, pages: 1 }); }
      else console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only; doSearch is re-created each render
  useEffect(() => { doSearch(); }, []);

  const handleSearch = (e) => { e?.preventDefault(); doSearch(1); };

  const handleExport = async () => {
    try {
      const { data } = await api.post('/export/csv', {
        query, postcode_district: district, ch_status: chStatus,
        digital_tier: digitalTier, sector, limit: 10
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `stockton_search_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-6 space-y-4" data-testid="search-page">
      {searchParams.get('intent') === 'claim' && (
        <div className="rounded-[10px] border border-forest/20 bg-forest/[0.06] p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="claim-intent-banner">
          <div className="flex-1">
            <p className="font-display text-sm font-extrabold text-ink">Step 1 of 5 · Find your business</p>
            <p className="font-body text-xs text-ink/60 mt-0.5">Search by name, postcode or Companies House number, then press <strong className="text-ink">Claim →</strong> on your listing. Verified owners are ranked first in every sector, category and postcode search.</p>
          </div>
          <span className="font-body text-[10px] uppercase tracking-wider text-forest font-semibold whitespace-nowrap">Found → Verify → Claim → Enrich → Ranked</span>
        </div>
      )}
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink">Business Search</h1>
        <p className="font-body text-sm text-ink/60 font-medium mt-1">Find and Verify Any Business</p>
        <p className="font-body text-sm text-ink/50 max-w-3xl leading-relaxed mt-2">
          Search every registered business across the active scope. Filter by postcode, Companies House status, digital tier, or sector to identify where support is needed most.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="glass-panel p-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink/40" strokeWidth={1.5} />
            <Input
              data-testid="search-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search by name, postcode, sector, or Companies House number…"
              className="pl-9 font-body text-sm"
            />
          </div>
          <Button data-testid="search-submit" type="submit" className="bg-brand-orange text-white hover:bg-brand-orange/90 font-body text-sm rounded-[4px]">
            Search
          </Button>
          <Button type="button" variant="outline" size="sm" className="font-body text-xs rounded-[4px]" onClick={() => setShowFilters(!showFilters)}>
            <Filter className="w-3.5 h-3.5 mr-1" /> Filters
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-ink/10">
            <div>
              <label className="font-body text-xs text-ink/50 uppercase tracking-wider">District</label>
              <Select value={district} onValueChange={setDistrict}>
                <SelectTrigger data-testid="filter-district" className="font-body text-sm mt-1"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Districts</SelectItem>
                  {['TS17','TS18','TS19','TS20','TS21','TS22','TS23'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-ink/50 uppercase tracking-wider">CH Status</label>
              <Select value={chStatus} onValueChange={setChStatus}>
                <SelectTrigger data-testid="filter-status" className="font-body text-sm mt-1"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Dissolved">Dissolved</SelectItem>
                  <SelectItem value="Dormant">Dormant</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-ink/50 uppercase tracking-wider">Digital Tier</label>
              <Select value={digitalTier} onValueChange={setDigitalTier}>
                <SelectTrigger data-testid="filter-tier" className="font-body text-sm mt-1"><SelectValue placeholder="All" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">All Tiers</SelectItem>
                  <SelectItem value="Digitally Active">Digitally Active</SelectItem>
                  <SelectItem value="Partially Visible">Partially Visible</SelectItem>
                  <SelectItem value="Digitally Invisible">Digitally Invisible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-body text-xs text-ink/50 uppercase tracking-wider">Sector</label>
              <Input data-testid="filter-sector" value={sector} onChange={e => setSector(e.target.value)} placeholder="e.g. Retail" className="font-body text-sm mt-1" />
            </div>
          </div>
        )}
      </form>

      {/* District Reality banner — shown when a district filter is active */}
      {district && districtMeta && (
        <div
          className="relative rounded-[6px] p-4 sm:p-5 bg-gradient-to-br from-[#090909] to-[#2A2620] text-white border-l-4 border-brand-orange shadow-[0_4px_16px_-4px_rgba(0,0,0,0.18)]"
          data-testid={`district-reality-banner-${district}`}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-display text-xs font-extrabold tracking-wider text-brand-orange uppercase">{district} · District Reality</span>
                {districtMeta.risk && (
                  <span className={`text-[10px] font-body px-1.5 py-0.5 rounded-[3px] uppercase tracking-wider ${
                    districtMeta.risk === 'Low' ? 'bg-status-green/20 text-status-green' :
                    districtMeta.risk === 'Medium' ? 'bg-status-amber/20 text-status-amber' :
                    'bg-status-red/20 text-status-red'
                  }`}>{districtMeta.risk} Risk</span>
                )}
              </div>
              <p className="font-display text-base sm:text-lg font-extrabold leading-tight">{districtMeta.name}</p>
              <p className="font-body text-sm text-white/70 mt-1">
                <span className="text-white font-semibold">{districtMeta.invisible?.toLocaleString()}</span> of{' '}
                <span className="text-white font-semibold">{districtMeta.total?.toLocaleString()}</span> businesses
                {districtMeta.invisiblePct != null && (
                  <> · <span className="text-brand-orange font-bold">{districtMeta.invisiblePct.toFixed(0)}% digitally invisible</span></>
                )}
              </p>
              <p className="font-body text-[11px] text-white/45 italic mt-1.5">
                Coverage is not connectivity. Share this district's data with stakeholders.
              </p>
            </div>
            <DistrictShareInline district={district} name={districtMeta.name} invisiblePct={districtMeta.invisiblePct} />
          </div>
        </div>
      )}

      {/* Results header */}
      <div className="flex items-center justify-between">
        <span className="font-body text-xs text-ink/50" data-testid="results-count">
          {results.total.toLocaleString()} businesses found · Page {results.page} of {results.pages} · <span className="text-forest">✓ Verified owners ranked first</span>
        </span>
        <Button data-testid="export-csv-btn" variant="outline" size="sm" onClick={handleExport} className="font-body text-xs rounded-[4px]">
          <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
        </Button>
      </div>

      {/* Results list */}
      <div className="space-y-2">
        {loading ? (
          <div className="solid-card p-8 text-center">
            <span className="font-body text-sm text-ink/40">Searching...</span>
          </div>
        ) : quotaHit ? (
          <QuotaLimitCard detail={quotaHit} context="Business search" />
        ) : results.businesses.length === 0 ? (
          <div className="solid-card p-8 text-center" data-testid="empty-search-state">
            <Search className="w-8 h-8 text-ink/20 mx-auto mb-3" />
            <p className="font-body text-sm text-ink/50">No businesses match the current filters. Try broadening the search — remove a filter, or search by postcode district (e.g. TS18).</p>
          </div>
        ) : results.businesses.map(biz => (
          <div key={biz.id} role="link" tabIndex={0} onClick={() => navigate(`/business/${biz.id}`)} onKeyDown={e => e.key === 'Enter' && navigate(`/business/${biz.id}`)} data-testid={`business-card-${biz.id}`}>
            <div className="solid-card p-4 hover:-translate-y-[1px] transition-all cursor-pointer group">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-body text-sm font-medium text-ink group-hover:text-brand-orange transition-colors truncate">{biz.company_name}</span>
                    <DigitalTierBadge tier={biz.digital_tier} />
                    <span className={`text-xs font-body px-1.5 py-0.5 rounded-[4px] ${biz.ch_status === 'Active' ? 'bg-status-green/10 text-status-green' : biz.ch_status === 'Dissolved' ? 'bg-status-red/10 text-status-red' : 'bg-status-amber/10 text-status-amber'}`}>
                      {biz.ch_status}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 font-body text-xs text-ink/40">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {biz.postcode}</span>
                    <span className="flex items-center gap-1"><Building2 className="w-3 h-3" /> {biz.sector}</span>
                    <span className="flex items-center gap-1"><Wifi className="w-3 h-3" /> {biz.broadband_type} · {biz.download_speed}Mbps</span>
                  </div>
                </div>
                <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                  <span className="font-display text-xl font-extrabold" style={{ color: biz.digital_score >= 4 ? '#1A4D3A' : biz.digital_score >= 2 ? '#B45309' : '#DC2626' }}>
                    {biz.digital_score}/5
                  </span>
                  <BDAFDots score={biz.digital_score} size={6} />
                  <ClaimBadge business={biz} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {results.pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => doSearch(page - 1)} className="font-body text-xs rounded-[4px]">
            <ChevronLeft className="w-3.5 h-3.5" /> Previous
          </Button>
          <span className="font-body text-xs text-ink/50">Page {page} of {results.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= results.pages} onClick={() => doSearch(page + 1)} className="font-body text-xs rounded-[4px]">
            Next <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
